import { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  Grid,
  Avatar,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestoreIcon from "@mui/icons-material/Restore";
import axios from "axios";
import apiService from "../../../service/Api";
import ConvertToBookingDialog from "./QuoteConverttoBookingDialog";
import { tokens } from "../../../theme";

interface QuoteResponse {
  id: number;
  request_type: string;
  contact?: {
    id: number;
    first_name: string;
    last_name: string;
    email?: string | null;
    phone?: string | null;
  };

  event: {
    id: number;
    title: string;

  };
  deceased?: {
    first_name: string;
    surname: string;
    nickname?: string;
    date_of_birth?: string;
    date_of_death?: string;
    person_image?: string;
  } | null;
  event_datetime: string;
  venue_name: string;
  venue_latitude: string;
  venue_longitude: string;
  created_at: string;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateWithWeekday = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const formattedDate = formatDate(dateString);
  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${formattedDate}, ${time}`;
};

const QuoteRequestView = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const user = useSelector((state: RootState) => state.auth.user);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [convertOpen, setConvertOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [whatsappTemplate, setWhatsappTemplate] = useState("");
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);

  // AI assistant states
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGeneratedText, setAiGeneratedText] = useState("");
  const [aiError, setAiError] = useState("");
  const [originalMessage, setOriginalMessage] = useState("");

  const handleOpenConvertDialog = () => {
    setConvertOpen(true);
  };

  const { id } = useParams();
  const navigate = useNavigate();

  const normalizePhoneForWhatsapp = (phone?: string) => {
    if (!phone) return "";
    let cleaned = phone.replace(/[^\d+]/g, "");
    cleaned = cleaned.replace(/^\+/, "");
    if (cleaned.startsWith("0")) {
      cleaned = `263${cleaned.slice(1)}`;
    }
    return cleaned;
  };

  const DEFAULT_WHATSAPP_TEMPLATE = "Hi [Client Name], this is [Your Name] from Muzukuru. I am contacting you regarding [Topic].";

  const getStoredTemplate = () => {
    const stored = localStorage.getItem("whatsapp_quote_template");
    if (!stored || stored === "Hi [Client Name], this is [Your Name]. I am contacting you regarding your quote request #[Quote ID].") {
      return DEFAULT_WHATSAPP_TEMPLATE;
    }
    return stored;
  };

  const compileTemplate = (templateStr: string, clientNameStr: string, userNameStr: string, topicStr: string) => {
    return templateStr
      .replace(/\[Client Name\]/g, clientNameStr)
      .replace(/\[Your Name\]/g, userNameStr)
      .replace(/\[Topic\]/g, topicStr)
      .replace(/#?\[Quote ID\]/g, "");
  };

  const handleOpenWhatsappDialog = () => {
    const currentTemplate = getStoredTemplate();
    setWhatsappTemplate(currentTemplate);
    const userNameStr = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "";
    const clientNameStr = quote?.contact?.first_name || "Client";

    let topicStr = "the quote request you submitted";
    if (quote) {
      if (quote.request_type === "SPONSORSHIP") {
        topicStr = `the quote request for the event "${quote.event.title}"`;
      } else if (quote.deceased) {
        topicStr = `the quote request regarding the late ${quote.deceased.first_name} ${quote.deceased.surname}`;
      }
    }

    const compiled = compileTemplate(currentTemplate, clientNameStr, userNameStr, topicStr);
    setWhatsappMessage(compiled);
    setOriginalMessage(compiled);
    setShowTemplateEditor(false);

    // Reset AI state
    setAiPanelOpen(false);
    setAiPrompt("");
    setAiGeneratedText("");
    setAiError("");
    setWhatsappOpen(true);
  };

  const STANDARD_PRICING_TEXT = `Our *most popular package* is our:
*Muzukuru Pro package starting at $699* 
This is a 3 Camera package and includes *shots of the Audience* - this really helps relatives and friends following online to feel that they are part of the service.

Our starting price for a live broadcast including a site visit is $499 - This is our *Muzukuru X Package* with only 2 cameras - *(No Audience shots)* - family and friends really appreciate seeing others there.

We have many additional services should you require them we would be happy to provide these.

*Your first step* is to send us a photo 
(So that we can use the photo to create a cover page for the video and to create a link to share with family and friends.)`;

  const handleAppendPricing = () => {
    setWhatsappMessage((prev) => {
      if (!prev.trim()) return STANDARD_PRICING_TEXT;
      return `${prev.trim()}\n\n${STANDARD_PRICING_TEXT}`;
    });
  };

  const handleGenerateWithAI = async (overridePrompt?: string) => {
    setAiError("");
    setAiLoading(true);

    const apiKey =
      process.env.REACT_APP_GEMINI_API_KEY ||
      process.env.GEMINI_API_KEY ||
      (window as unknown as { GEMINI_API_KEY?: string }).GEMINI_API_KEY;

    if (!apiKey) {
      setAiError("AI feature is currently unavailable because the API key is not configured in the environment.");
      setAiLoading(false);
      return;
    }

    const userNameStr = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "";
    const clientNameStr = quote?.contact?.first_name || "Client";
    const deceasedName = quote?.deceased
      ? `${quote.deceased.first_name} ${quote.deceased.surname}`
      : "N/A";
    const eventDate = quote?.event_datetime ? formatDate(quote.event_datetime) : "N/A";
    const eventTime = quote?.event_datetime ? new Date(quote.event_datetime).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }) : "N/A";

    let topicStr = "the quote request you submitted";
    if (quote) {
      if (quote.request_type === "SPONSORSHIP") {
        topicStr = `the quote request for the event "${quote.event.title}"`;
      } else if (quote.deceased) {
        topicStr = `the quote request regarding the late ${quote.deceased.first_name} ${quote.deceased.surname}`;
      }
    }

    const promptText = overridePrompt || aiPrompt;

    const fullPrompt = `You are an AI assistant helping a staff member at Muzukuru Funeral Streaming (a funeral home and event streaming service provider) write a professional, respectful, and clear WhatsApp message to a client.

Context:
- Client Name: ${clientNameStr}
- Staff Member (Your Name): ${userNameStr}
- Topic: ${topicStr}
- Event Title: ${quote?.request_type === "SPONSORSHIP" ? quote.event.title : "Funeral/Memorial Service"}
- Deceased Name: ${deceasedName}
- Event Date: ${eventDate}
- Event Time: ${eventTime}
- Location: ${quote?.venue_name || "N/A"}

Standard Pricing & Package Details (Reference when asked to include pricing, packages, or costs):
- Muzukuru Pro Package (Most Popular): Starting at $699. 3 Camera package, includes shots of the Audience (helps relatives and friends following online feel part of the service).
- Muzukuru X Package: Starting price $499 for live broadcast including a site visit. 2 cameras (No Audience shots).
- Additional Services: We have many additional services should you require them, we would be happy to provide these.
- First Step Required: Client's first step is to send us a photo so we can use it to create a cover page for the video and create a link to share with family and friends.

Current message draft:
"${whatsappMessage}"

User instruction:
"${promptText || "Generate a standard polite message about their quote request"}"

Please generate only the message text itself. Do not include any subject line, quotes around the message, or conversational introductory/concluding remarks from you (the AI). Write it in a style suitable for WhatsApp (clear, concise, professional, empathetic, and cleanly formatted using WhatsApp *bold* markers where appropriate).`;

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
        {
          contents: [
            {
              parts: [{ text: fullPrompt }],
            },
          ],
        }
      );

      const generated = response.data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (generated) {
        let cleanText = generated.trim();
        if (cleanText.startsWith("```")) {
          cleanText = cleanText.replace(/^```[a-zA-Z]*\n/, "").replace(/\n```$/, "");
        }
        setAiGeneratedText(cleanText.trim());
      } else {
        setAiError("Failed to generate content. Please try again.");
      }
    } catch (err: unknown) {
      console.error("API Error", err);
      let errMsg = "An error occurred while communicating with AI.";
      if (axios.isAxiosError(err)) {
        errMsg = err.response?.data?.error?.message || err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      setAiError(errMsg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleTemplateChange = (newTemplate: string) => {
    setWhatsappTemplate(newTemplate);
    const userNameStr = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "";
    const clientNameStr = quote?.contact?.first_name || "Client";

    let topicStr = "the quote request you submitted";
    if (quote) {
      if (quote.request_type === "SPONSORSHIP") {
        topicStr = `the quote request for the event "${quote.event.title}"`;
      } else if (quote.deceased) {
        topicStr = `the quote request regarding the late ${quote.deceased.first_name} ${quote.deceased.surname}`;
      }
    }

    const compiled = compileTemplate(newTemplate, clientNameStr, userNameStr, topicStr);
    setWhatsappMessage(compiled);
  };

  const handleSaveTemplateAndSend = () => {
    if (showTemplateEditor) {
      localStorage.setItem("whatsapp_quote_template", whatsappTemplate);
    }

    if (quote?.contact?.phone) {
      const normalizedPhone = normalizePhoneForWhatsapp(quote.contact.phone);
      if (normalizedPhone) {
        const encoded = encodeURIComponent(whatsappMessage || "");
        const url = `https://wa.me/${normalizedPhone}?text=${encoded}`;
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
    setWhatsappOpen(false);
  };

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await apiService.get<QuoteResponse>(
          `/api/bookings/quote-request/${id}/`,
        );
        setQuote(response.data);
      } catch (err) {
        console.error("Failed to fetch quote", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  if (loading) return <Typography>Loading...</Typography>;
  if (!quote) return <Typography>No quote found.</Typography>;

  // Map backend fields for display
  const eventDate = formatDate(quote.event_datetime);
  const eventTime = new Date(quote.event_datetime).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const belovedName = quote.deceased
    ? `${quote.deceased.first_name} ${quote.deceased.surname}`
    : "N/A";

  const clientName = quote.contact
    ? `${quote.contact.first_name} ${quote.contact.last_name}`
    : "N/A";
  const clientPhone = quote.contact?.phone || "N/A";
  const clientEmail = quote.contact?.email || "N/A";

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
        <Button
          startIcon={<WhatsAppIcon />}
          sx={{
            border: 2,
            borderColor: colors.primary[700],
            color: colors.primary[700],
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: "#25D366",
              color: "#25D366",
              bgcolor: "rgba(37, 211, 102, 0.04)"
            }
          }}
          onClick={handleOpenWhatsappDialog}
        >
          WhatsApp Client
        </Button>
        <Button
          sx={{ border: 2, borderColor: colors.primary[700], color: colors.primary[700] }}
          onClick={handleOpenConvertDialog}
        >
          Convert to Booking
        </Button>
        <Button sx={{ border: 2, borderColor: colors.primary[700], color: colors.primary[700] }}>
          Generate Memorial Pages
        </Button>

        <Button
          onClick={() => {
            navigate("/proposals/proposals-builder", {
              state: {
                fromQuoteRequest: true,
                quoteId: quote.id,
                clientId: quote?.contact?.id,
                deceased: quote.deceased
                  ? {
                    first_name: quote.deceased.first_name,
                    surname: quote.deceased.surname,
                    date_of_birth: quote.deceased.date_of_birth,
                    date_of_death: quote.deceased.date_of_death,
                  }
                  : null,
                event: {
                  datetime: quote.event_datetime,
                  venue_name: quote.venue_name,
                },
              },
            });
          }}
          sx={{ border: 2, borderColor: colors.primary[700], color: colors.primary[700] }}
        >
          Create Proposal
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 0 }}>
        <Box sx={{ background: "#614338", color: "#fff", p: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Quote Request Details
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Submitted on {formatDateTime(quote.created_at)}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            ID: {quote.id}
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Event Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography>
                <strong>Event Date:</strong>{" "}
                {formatDateWithWeekday(quote.event_datetime)}
              </Typography>

              <Typography>
                <strong>Start Time:</strong> {eventTime}
              </Typography>
              <Typography>
                <strong>Location:</strong> {quote.venue_name}
              </Typography>
              <Typography>
                <strong>Venue Coordinates:</strong> {quote.venue_latitude},{" "}
                {quote.venue_longitude}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Personalization Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography sx={{ mb: 2 }}>
                <strong>{quote.request_type === "SPONSORSHIP" ? "Event Title" : "Beloved Name:"}:</strong> {quote.request_type === "SPONSORSHIP" ? quote.event.title : quote.deceased?.first_name}
              </Typography>
              {quote.request_type === "SPONSORSHIP" && (
                <Box sx={{ width: "100%", height: "100%" }}>
                  <Avatar
                    src="https://i.pinimg.com/736x/d4/f9/70/d4f970750aadd73c1ab1fdee51fc46c1.jpg"
                    alt={belovedName}
                    variant="rounded"
                    sx={{
                      width: 120,
                      height: 100,
                    }}
                  >
                    {quote.event?.title?.[0]}
                  </Avatar>
                </Box>
              )}
              {quote.deceased && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    src={quote.deceased.person_image || "https://i.pinimg.com/736x/d4/f9/70/d4f970750aadd73c1ab1fdee51fc46c1.jpg"}
                    alt={belovedName}
                    sx={{ width: 64, height: 64 }}

                  >   {quote.request_type === "SPONSORSHIP" ? quote.event.title[0] : quote.deceased.first_name[0]} </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {belovedName}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Client Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography>
                <strong>Client Name:</strong> {clientName}
              </Typography>
              <Typography>
                <strong>Phone:</strong> {clientPhone}
              </Typography>
              <Typography>
                <strong>Email:</strong> {clientEmail}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, bgcolor: "#f9fafb", borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Complete Information Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Request ID:</strong>
                </Typography>
                <Typography>{quote.id}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Submitted:</strong>
                </Typography>
                <Typography>
                  {formatDateTime(quote.created_at)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Event Date:</strong>
                </Typography>
                <Typography>{eventDate}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Event Time:</strong>
                </Typography>
                <Typography>{eventTime}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Location:</strong>
                </Typography>
                <Typography>{quote.venue_name}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography>
                  <strong>Coordinates:</strong>
                </Typography>
                <Typography>
                  {quote.venue_latitude}, {quote.venue_longitude}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      <ConvertToBookingDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        quoteId={quote.id}
      />

      <Dialog
        open={whatsappOpen}
        onClose={() => setWhatsappOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Send WhatsApp Message</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Send a pre-filled WhatsApp message to <strong>{clientName}</strong> ({clientPhone}).
          </Typography>

          <TextField
            label="Message Content"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={whatsappMessage}
            onChange={(e) => setWhatsappMessage(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Button
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setAiPanelOpen(!aiPanelOpen)}
              sx={{
                bgcolor: "#614338",
                color: "white",
                fontWeight: 600,
                borderRadius: 2,
                px: 2,
                py: 0.8,
                textTransform: "none",
                boxShadow: "0 3px 5px 2px rgba(97, 67, 56, .2)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#4d342c",
                  transform: "scale(1.02)",
                }
              }}
            >
              {aiPanelOpen ? "Close AI Assistant" : "Write with AI"}
            </Button>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                size="small"
                onClick={handleAppendPricing}
                sx={{
                  textTransform: "none",
                  color: "#614338",
                  fontWeight: 600,
                  border: "1px dashed rgba(97, 67, 56, 0.4)",
                  px: 1.5,
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(97, 67, 56, 0.05)" }
                }}
              >
                + Insert Standard Pricing
              </Button>

              {whatsappMessage !== originalMessage && (
                <Button
                  size="small"
                  startIcon={<RestoreIcon />}
                  onClick={() => setWhatsappMessage(originalMessage)}
                  sx={{ textTransform: "none", color: "text.secondary" }}
                >
                  Reset to Original
                </Button>
              )}
            </Box>
          </Box>

          <Collapse in={aiPanelOpen} timeout="auto" unmountOnExit>
            <Box
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(97, 67, 56, 0.04)",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(97, 67, 56, 0.12)"}`,
                boxShadow: theme.palette.mode === "dark" ? "none" : "inset 0 1px 3px rgba(0,0,0,0.02)",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: "#614338" }} />
                  Muzukuru AI Assistant
                </Typography>
              </Box>

              <TextField
                label="What would you like the AI to write/change?"
                placeholder="e.g., Make it sound more empathetic, translate to Shona, include package pricing options..."
                multiline
                rows={2}
                fullWidth
                variant="outlined"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                sx={{ mb: 1.5 }}
                disabled={aiLoading}
              />

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {[
                  { label: "🏷️ Include Pricing", prompt: "Include our standard package pricing details ($699 Muzukuru Pro package starting price with 3 cameras & audience shots, $499 Muzukuru X package with 2 cameras), mention additional services are available, and ask them to send us a photo as the first step for the video cover page and sharing link." },
                  { label: "💼 Professional", prompt: "Make this message sound more professional, polite, and official." },
                  { label: "❤️ Empathetic", prompt: "Make this message very warm, supportive, and empathetic." },
                  { label: "⚡ Short & Direct", prompt: "Make this message extremely concise and to the point." },
                  { label: "📞 Request Callback", prompt: "Add a request asking them to call us back when they get a chance." },
                ].map((item) => (
                  <Chip
                    key={item.label}
                    label={item.label}
                    onClick={() => {
                      setAiPrompt(item.prompt);
                      handleGenerateWithAI(item.prompt);
                    }}
                    disabled={aiLoading}
                    clickable
                    size="small"
                    sx={{
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button
                  onClick={() => handleGenerateWithAI()}
                  variant="contained"
                  disabled={aiLoading}
                  startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  sx={{
                    bgcolor: "#614338",
                    color: "white",
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    "&:hover": {
                      bgcolor: "#4d342c",
                    }
                  }}
                >
                  {aiLoading ? "Generating..." : "Generate with AI"}
                </Button>
              </Box>

              {aiError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {aiError}
                </Alert>
              )}

              {aiGeneratedText && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    position: "relative",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} mb={1}>
                    AI Suggestion:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                    {aiGeneratedText}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => setAiGeneratedText("")}
                      sx={{ textTransform: "none" }}
                    >
                      Discard
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setWhatsappMessage(aiGeneratedText);
                        setAiGeneratedText("");
                        setAiPanelOpen(false);
                      }}
                      sx={{
                        textTransform: "none",
                        bgcolor: "#25D366",
                        color: "white",
                        "&:hover": { bgcolor: "#128C7E" },
                      }}
                    >
                      Apply Message
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>

          <Box sx={{ mb: 1 }}>
            <Button
              size="small"
              onClick={() => setShowTemplateEditor(!showTemplateEditor)}
              sx={{ textTransform: "none", color: colors.primary[700] }}
            >
              {showTemplateEditor ? "Hide Template Settings" : "Configure Default Template"}
            </Button>
          </Box>

          {showTemplateEditor && (
            <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Default Message Template
              </Typography>
              <TextField
                label="Template String"
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                value={whatsappTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                helperText="Use placeholders: [Client Name], [Your Name], [Topic]"
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Example: Hi [Client Name], this is [Your Name] from Muzukuru regarding [Topic].
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWhatsappOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplateAndSend}
            variant="contained"
            startIcon={<WhatsAppIcon />}
            sx={{
              bgcolor: "#25D366",
              color: "#fff",
              "&:hover": {
                bgcolor: "#128C7E"
              }
            }}
          >
            Open WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteRequestView;
